import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Textarea,
  Typography,
} from '@mui/joy';
import { Link, Navigate } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import { useProfile } from '../../../hooks/useProfile';
import { IconWrapper } from '../../../components/IconWrapper';
import {
  BILL_PAYMENT_TERMS_LABELS,
  type BillPaymentTerms,
  type DirectoryAccount,
} from '../accounts.model';
import { accountsService } from '../accounts.service';
import {
  CURRENT_FLEET_POLICY_VERSION,
  PAY_STRUCTURE_LABELS,
  PERSONAL_ID_TYPES,
  dueStatus,
  emptyRecord,
  formatUkDate,
  newPersonalIdKey,
  personalIdDueDate,
  recordAlerts,
  ukToday,
  validateRecord,
  type DueStatus,
  type PayStructure,
  type PersonalId,
  type SubcontractorRecord,
} from '../subcontractorRecords.model';
import { subcontractorRecordsService } from '../subcontractorRecords.service';

const PAY_STRUCTURES = Object.keys(PAY_STRUCTURE_LABELS) as PayStructure[];
const PAY_TERMS = Object.keys(BILL_PAYMENT_TERMS_LABELS) as BillPaymentTerms[];

const DUE_COLOR: Record<DueStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  none: 'neutral',
  ok: 'success',
  soon: 'warning',
  overdue: 'danger',
};

const yesNo = (value: boolean) => (
  <Chip size="sm" variant="soft" color={value ? 'success' : 'neutral'}>{value ? 'Yes' : 'No'}</Chip>
);

const money = (n: number | null) => (n == null ? '' : `£${n.toFixed(2)}`);

function payLabel(record: SubcontractorRecord | null): string {
  if (!record || record.payStructure === 'not_set') return '—';
  const rate = money(record.payRate);
  const per = record.payStructure === 'day_rate' ? '/day' : record.payStructure === 'hourly' ? '/hr' : '';
  return rate ? `${PAY_STRUCTURE_LABELS[record.payStructure]} · ${rate}${per}` : PAY_STRUCTURE_LABELS[record.payStructure];
}

type EditorProps = {
  account: DirectoryAccount | null;
  record: SubcontractorRecord | null;
  onClose: () => void;
  onSaved: (record: SubcontractorRecord, payTerms: BillPaymentTerms) => void;
};

const SubcontractorEditor: React.FC<EditorProps> = ({ account, record, onClose, onSaved }) => {
  const [draft, setDraft] = useState<SubcontractorRecord | null>(null);
  const [payTerms, setPayTerms] = useState<BillPaymentTerms>('none');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = ukToday();

  useEffect(() => {
    if (!account) return;
    setDraft(record ? { ...record, personalIds: record.personalIds.map((row) => ({ ...row })) } : emptyRecord(account.id));
    setPayTerms(account.billPaymentTerms);
    setError(null);
  }, [account, record]);

  if (!account || !draft) return null;

  const set = <K extends keyof SubcontractorRecord>(key: K, value: SubcontractorRecord[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };
  const date = (key: keyof SubcontractorRecord) => ({
    type: 'date',
    value: (draft[key] as string | null) || '',
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => set(key, (event.target.value || null) as never),
  });
  const textField = (key: keyof SubcontractorRecord) => ({
    value: (draft[key] as string | null) || '',
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => set(key, event.target.value as never),
  });
  const setId = (id: string, patch: Partial<PersonalId>) => {
    set('personalIds', draft.personalIds.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const save = async () => {
    const problem = validateRecord(draft);
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError(null);
    try {
      const saved = await subcontractorRecordsService.save(draft);
      if (payTerms !== account.billPaymentTerms) await subcontractorRecordsService.setPayTerms(account.id, payTerms);
      onSaved(saved, payTerms);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={saving ? undefined : onClose}>
      <ModalDialog sx={{ width: 'min(860px, 96vw)', maxHeight: '92vh', overflow: 'auto' }}>
        <DialogTitle>{account.companyName}</DialogTitle>
        <DialogContent>
          <Stack gap={2.5}>
            {error && <Alert color="danger">{error}</Alert>}

            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>Contract</Typography>
              <Grid container spacing={1.5}>
                <Grid xs={12} sm={4}>
                  <FormControl><FormLabel>Start date</FormLabel><Input {...date('contractStartDate')} /></FormControl>
                </Grid>
                <Grid xs={12} sm={4}>
                  <FormControl>
                    <FormLabel>End / expiry date</FormLabel>
                    <Input {...date('contractEndDate')} />
                    <FormHelperText>Leave blank for a rolling contract.</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={4}>
                  <FormControl><FormLabel>Term</FormLabel><Input {...textField('contractTerm')} placeholder="e.g. 12 months, rolling" /></FormControl>
                </Grid>
                <Grid xs={12} sm={4}>
                  <Checkbox
                    label="Contract issued"
                    checked={draft.contractIssued}
                    onChange={(event) => {
                      set('contractIssued', event.target.checked);
                      if (event.target.checked && !draft.contractIssuedOn) set('contractIssuedOn', today);
                    }}
                    sx={{ mt: { sm: 3.5 } }}
                  />
                </Grid>
                <Grid xs={12} sm={4}>
                  <FormControl disabled={!draft.contractIssued}><FormLabel>Issued on</FormLabel><Input {...date('contractIssuedOn')} /></FormControl>
                </Grid>
                <Grid xs={12} sm={4}>
                  <FormControl disabled={!draft.contractIssued}><FormLabel>Signed copy back</FormLabel><Input {...date('contractSignedOn')} /></FormControl>
                </Grid>
              </Grid>
            </Box>

            <Divider />
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>Fleet</Typography>
              <Grid container spacing={1.5}>
                <Grid xs={12} sm={4}>
                  <Checkbox label="Fleet driver" checked={draft.fleetDriver} onChange={(event) => set('fleetDriver', event.target.checked)} sx={{ mt: { sm: 3.5 } }} />
                </Grid>
                <Grid xs={12} sm={4}>
                  <FormControl disabled={!draft.fleetDriver}><FormLabel>Vehicle registration</FormLabel><Input {...textField('vehicleRegistration')} /></FormControl>
                </Grid>
                <Grid xs={12} sm={4}>
                  <FormControl disabled={!draft.fleetDriver}><FormLabel>Licence check date</FormLabel><Input {...date('licenceCheckDate')} /></FormControl>
                </Grid>
                <Grid xs={12} sm={4}>
                  <Checkbox
                    label="Fleet Driver Policy issued"
                    checked={draft.fleetPolicyIssued}
                    onChange={(event) => {
                      set('fleetPolicyIssued', event.target.checked);
                      if (event.target.checked) {
                        if (!draft.fleetPolicyIssuedOn) set('fleetPolicyIssuedOn', today);
                        if (!draft.fleetPolicyVersion) set('fleetPolicyVersion', CURRENT_FLEET_POLICY_VERSION);
                      }
                    }}
                    sx={{ mt: { sm: 3.5 } }}
                  />
                </Grid>
                <Grid xs={12} sm={4}>
                  <FormControl disabled={!draft.fleetPolicyIssued}><FormLabel>Issued on</FormLabel><Input {...date('fleetPolicyIssuedOn')} /></FormControl>
                </Grid>
                <Grid xs={12} sm={4}>
                  <FormControl disabled={!draft.fleetPolicyIssued}>
                    <FormLabel>Version</FormLabel>
                    <Input {...textField('fleetPolicyVersion')} />
                    {draft.fleetPolicyIssued && draft.fleetPolicyVersion && draft.fleetPolicyVersion !== CURRENT_FLEET_POLICY_VERSION && (
                      <FormHelperText>Current policy is {CURRENT_FLEET_POLICY_VERSION}.</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
              {draft.fleetDriver && !draft.fleetPolicyIssued && (
                <Alert color="warning" variant="soft" size="sm" sx={{ mt: 1.5 }}>
                  A fleet driver should be issued the Fleet Driver Policy before handover.
                </Alert>
              )}
            </Box>

            <Divider />
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography level="title-sm">Personal IDs and renewals</Typography>
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={() => set('personalIds', [...draft.personalIds, {
                    id: newPersonalIdKey(), type: '', reference: null, obtainedOn: null, expiresOn: null, renewalMonths: null,
                  }])}
                >
                  Add ID
                </Button>
              </Stack>
              {draft.personalIds.length === 0 && (
                <Typography level="body-sm" textColor="neutral.500">None recorded.</Typography>
              )}
              <Stack gap={1.25}>
                {draft.personalIds.map((row) => {
                  const due = personalIdDueDate(row);
                  const status = dueStatus(due, today);
                  return (
                    <Sheet key={row.id} variant="outlined" sx={{ p: 1.25, borderRadius: 'sm' }}>
                      <Grid container spacing={1} alignItems="flex-end">
                        <Grid xs={12} sm={4}>
                          <FormControl>
                            <FormLabel>Type</FormLabel>
                            <Autocomplete
                              freeSolo
                              options={PERSONAL_ID_TYPES}
                              inputValue={row.type}
                              onInputChange={(_, value) => setId(row.id, { type: value })}
                              placeholder="e.g. Driving licence"
                            />
                          </FormControl>
                        </Grid>
                        <Grid xs={12} sm={8}>
                          <FormControl>
                            <FormLabel>Reference / number</FormLabel>
                            <Input value={row.reference || ''} onChange={(event) => setId(row.id, { reference: event.target.value })} />
                          </FormControl>
                        </Grid>
                        <Grid xs={6} sm={3}>
                          <FormControl>
                            <FormLabel>Obtained</FormLabel>
                            <Input type="date" value={row.obtainedOn || ''} onChange={(event) => setId(row.id, { obtainedOn: event.target.value || null })} />
                          </FormControl>
                        </Grid>
                        <Grid xs={6} sm={3}>
                          <FormControl>
                            <FormLabel>Expires</FormLabel>
                            <Input type="date" value={row.expiresOn || ''} onChange={(event) => setId(row.id, { expiresOn: event.target.value || null })} />
                          </FormControl>
                        </Grid>
                        <Grid xs={6} sm={3}>
                          <FormControl>
                            <FormLabel>Renew every</FormLabel>
                            <Input
                              type="number"
                              value={row.renewalMonths ?? ''}
                              onChange={(event) => setId(row.id, { renewalMonths: event.target.value ? Number(event.target.value) : null })}
                              endDecorator="months"
                              slotProps={{ input: { min: 1 } }}
                            />
                          </FormControl>
                        </Grid>
                        <Grid xs={6} sm={3}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 36 }}>
                            <Chip size="sm" variant="soft" color={DUE_COLOR[status]}>
                              {due ? `Due ${formatUkDate(due)}` : 'No due date'}
                            </Chip>
                            <IconButton
                              size="sm"
                              color="danger"
                              variant="plain"
                              aria-label="Remove ID"
                              onClick={() => set('personalIds', draft.personalIds.filter((item) => item.id !== row.id))}
                            >
                              <IconWrapper icon={FaTrash} />
                            </IconButton>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Sheet>
                  );
                })}
              </Stack>
            </Box>

            <Divider />
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>Clothing requirements</Typography>
              <Textarea minRows={2} {...textField('clothingRequirements')} placeholder="PPE, branded workwear, sizes, what has been issued…" />
            </Box>

            <Divider />
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>Pay</Typography>
              <Grid container spacing={1.5}>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Pay terms</FormLabel>
                    <Select value={payTerms} onChange={(_, value) => value && setPayTerms(value as BillPaymentTerms)}>
                      {PAY_TERMS.map((terms) => <Option key={terms} value={terms}>{BILL_PAYMENT_TERMS_LABELS[terms]}</Option>)}
                    </Select>
                    <FormHelperText>Saved on their account; sets the due date on their new bills.</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl><FormLabel>Pay terms notes</FormLabel><Input {...textField('payTermsNotes')} placeholder="e.g. invoice by Monday, BACS" /></FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Pay structure</FormLabel>
                    <Select value={draft.payStructure} onChange={(_, value) => value && set('payStructure', value as PayStructure)}>
                      {PAY_STRUCTURES.map((structure) => <Option key={structure} value={structure}>{PAY_STRUCTURE_LABELS[structure]}</Option>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl disabled={draft.payStructure === 'not_set'}>
                    <FormLabel>Rate</FormLabel>
                    <Input
                      type="number"
                      startDecorator="£"
                      endDecorator={draft.payStructure === 'day_rate' ? 'per day' : draft.payStructure === 'hourly' ? 'per hour' : undefined}
                      value={draft.payRate ?? ''}
                      onChange={(event) => set('payRate', event.target.value === '' ? null : Number(event.target.value))}
                      slotProps={{ input: { min: 0, step: '0.01' } }}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12}>
                  <FormControl>
                    <FormLabel>Pay structure notes</FormLabel>
                    <Textarea minRows={2} {...textField('payStructureNotes')} placeholder="Overtime, weekend rates, price-work rates, deductions…" />
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            <Divider />
            <FormControl>
              <FormLabel>Notes</FormLabel>
              <Textarea minRows={2} {...textField('notes')} />
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={save} loading={saving}>Save</Button>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>Cancel</Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

/**
 * Office only: contract, fleet, ID, clothing and pay details for every directory account
 * ticked "Keystone subcontractor". Fitters, labourers, suppliers and subbies never see it.
 */
const SubcontractorsPage: React.FC = () => {
  const { loading: profileLoading, isStaff, isSiteCrew, isSupplier, profile } = useProfile();
  const isOffice = isStaff && !isSiteCrew && !isSupplier && profile?.role !== 'document';
  const [accounts, setAccounts] = useState<DirectoryAccount[]>([]);
  const [records, setRecords] = useState<Map<string, SubcontractorRecord>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const today = ukToday();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [accountRows, recordRows] = await Promise.all([accountsService.list(), subcontractorRecordsService.list()]);
      setAccounts(accountRows.filter((account) => account.isKeystoneSubcontractor));
      setRecords(new Map(recordRows.map((row) => [row.accountId, row])));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subcontractors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isOffice) void load(); }, [isOffice, load]);

  const visible = useMemo(
    () => accounts.filter((account) => showInactive || account.isActive),
    [accounts, showInactive],
  );
  const editing = accounts.find((account) => account.id === editingId) || null;

  if (profileLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (!isOffice) return <Navigate to="/access-denied" replace />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
          <Stack spacing={0.5}>
            <Typography level="h3">Subcontractors</Typography>
            <Typography level="body-sm" textColor="neutral.600" sx={{ maxWidth: 760 }}>
              Contracts, fleet driving, ID renewals, clothing and pay for Keystone subcontractors. Office only. To add someone, tick &quot;Keystone subcontractor&quot; on their account in the Directory.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox size="sm" label="Show inactive" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />
            <Button variant="outlined" color="neutral" component={Link} to="/directory">Directory</Button>
          </Stack>
        </Stack>

        {error && <Alert color="danger">{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
            <Table hoverRow sx={{ minWidth: 1040, tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: '18%' }}>Subcontractor</th>
                  <th style={{ width: '17%' }}>Contract</th>
                  <th style={{ width: '13%' }}>Fleet</th>
                  <th style={{ width: '14%' }}>IDs</th>
                  <th style={{ width: '16%' }}>Pay</th>
                  <th style={{ width: '22%' }}>Needs attention</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((account) => {
                  const record = records.get(account.id) || null;
                  const alerts = recordAlerts(record, today);
                  const nextId = (record?.personalIds || [])
                    .map((row) => ({ row, due: personalIdDueDate(row) }))
                    .filter((item): item is { row: PersonalId; due: string } => !!item.due)
                    .sort((a, b) => a.due.localeCompare(b.due))[0];
                  return (
                    <tr key={account.id} onClick={() => setEditingId(account.id)} style={{ cursor: 'pointer' }}>
                      <td>
                        <Typography level="body-sm" fontWeight="lg">{account.companyName}</Typography>
                        {account.contactName && <Typography level="body-xs" textColor="neutral.500">{account.contactName}</Typography>}
                        {!account.isActive && <Chip size="sm" variant="outlined" color="neutral">Inactive</Chip>}
                      </td>
                      <td>
                        <Typography level="body-sm">
                          {record?.contractStartDate || record?.contractEndDate
                            ? `${formatUkDate(record.contractStartDate)} – ${record.contractEndDate ? formatUkDate(record.contractEndDate) : 'rolling'}`
                            : '—'}
                        </Typography>
                        <Typography level="body-xs" textColor="neutral.500">
                          {record?.contractIssued
                            ? `Issued ${formatUkDate(record.contractIssuedOn)}${record.contractSignedOn ? ' · signed' : ''}`
                            : 'Not issued'}
                          {record?.contractTerm ? ` · ${record.contractTerm}` : ''}
                        </Typography>
                      </td>
                      <td>
                        <Stack direction="row" gap={0.5} alignItems="center">
                          <Typography level="body-xs">Driver</Typography>{yesNo(!!record?.fleetDriver)}
                        </Stack>
                        <Stack direction="row" gap={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                          <Typography level="body-xs">Policy</Typography>{yesNo(!!record?.fleetPolicyIssued)}
                        </Stack>
                      </td>
                      <td>
                        <Typography level="body-sm">{record?.personalIds.length ? `${record.personalIds.length} on file` : '—'}</Typography>
                        {nextId && (
                          <Typography level="body-xs" textColor="neutral.500">
                            Next: {nextId.row.type || 'ID'} {formatUkDate(nextId.due)}
                          </Typography>
                        )}
                      </td>
                      <td>
                        <Typography level="body-sm">{payLabel(record)}</Typography>
                        <Typography level="body-xs" textColor="neutral.500">
                          {account.billPaymentTerms === 'none' ? 'Pay terms not set' : BILL_PAYMENT_TERMS_LABELS[account.billPaymentTerms]}
                        </Typography>
                      </td>
                      <td>
                        {alerts.length === 0 ? (
                          <Chip size="sm" variant="soft" color="success">All in order</Chip>
                        ) : (
                          <Stack direction="row" gap={0.5} flexWrap="wrap">
                            {alerts.map((alert) => (
                              <Chip key={alert.message} size="sm" variant="soft" color={alert.level}>{alert.message}</Chip>
                            ))}
                          </Stack>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <Typography level="body-sm" textColor="neutral.500" sx={{ py: 2, textAlign: 'center' }}>
                        No Keystone subcontractors yet. Tick &quot;Keystone subcontractor&quot; on an account in the Directory.
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Sheet>
        )}
      </Stack>

      {editing && (
        <SubcontractorEditor
          account={editing}
          record={records.get(editing.id) || null}
          onClose={() => setEditingId(null)}
          onSaved={(saved, payTerms) => {
            setRecords((current) => new Map(current).set(saved.accountId, saved));
            setAccounts((current) => current.map((account) => (
              account.id === saved.accountId ? { ...account, billPaymentTerms: payTerms } : account
            )));
            setEditingId(null);
          }}
        />
      )}
    </Box>
  );
};

export default SubcontractorsPage;
